import React, { useState } from 'react';
import { apiFetch } from '../../../utils/api';
import { STEPS } from '../CheckoutModal';

const EnquiryInfoStep = ({
  state,
  dispatch: modalDispatch,
  storeData,
  storeSlug,
  formatPrice,
  goToStep,
  goBack,
  cartItems,
  cartTotal
}) => {
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const merchantStates = state.merchantStates || [];

  // -- Form field handlers --
  const updateField = (field, value) => {
    modalDispatch({ type: 'UPDATE_FIELD', field, value });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // -- Variation label --
  const formatVariations = (variations) => {
    if (!variations || Object.keys(variations).length === 0) return '';
    return Object.entries(variations)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  // -- Validate --
  const validate = () => {
    const newErrors = {};
    if (!state.enquiryName?.trim()) {
      newErrors.enquiryName = 'Full name is required';
    }
    if (!state.enquiryPhone?.trim()) {
      newErrors.enquiryPhone = 'WhatsApp number is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -- Build WhatsApp message --
  const buildMessage = () => {
    let msg = 'Hi! I\'m interested in the following items:\n\n';

    cartItems.forEach((item, index) => {
      const product = item.product;
      const variationStr = formatVariations(item.variations);
      const variationPart = variationStr ? ` (${variationStr})` : '';
      const lineTotal = product.price * item.qty;
      msg += `${index + 1}. ${product.name}${variationPart} x${item.qty} — ${formatPrice(lineTotal)}\n`;
    });

    msg += `\nSubtotal: ${formatPrice(cartTotal)}\n`;
    msg += '\nMy Details:';
    msg += `\nName: ${state.enquiryName.trim()}`;
    msg += `\nPhone: ${state.enquiryPhone.trim()}`;

    if (state.enquiryEmail?.trim()) {
      msg += `\nEmail: ${state.enquiryEmail.trim()}`;
    }

    if (state.enquiryState?.trim()) {
      msg += `\nDelivery Location: ${state.enquiryState.trim()}`;
    }

    if (state.enquiryAddress?.trim()) {
      msg += `\nDelivery Address: ${state.enquiryAddress.trim()}`;
    }

    if (state.enquiryNote?.trim()) {
      msg += `\n\nNote: ${state.enquiryNote.trim()}`;
    }

    return msg;
  };

  // -- Send enquiry --
  const handleSendEnquiry = async () => {
    if (!validate()) return;

    setSending(true);

    try {
      await apiFetch('/abandoned-carts/track-checkout', {
        method: 'POST',
        body: JSON.stringify({
          storeSlug,
          type: 'ENQUIRY',
          customerName: state.enquiryName.trim(),
          customerPhone: state.enquiryPhone.trim(),
          customerEmail: state.enquiryEmail?.trim() || '',
          note: state.enquiryNote?.trim() || '',
          items: cartItems.map((item) => ({
            productId: item.product.id || item.product._id,
            name: item.product.name,
            price: item.product.price,
            quantity: item.qty,
            variations: item.variations || null,
            variantSku: item.variantSku || null
          })),
          subtotal: cartTotal
        })
      });
    } catch (err) {
      console.error('Failed to track enquiry:', err);
    }

    const message = buildMessage();
    const whatsappNumber = storeData.whatsappNumber.replace(/[^0-9]/g, '');
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');

    setSending(false);
  };

  return (
    <div className="cm-step">
      {/* Form */}
      <form className="cm-form" onSubmit={(e) => { e.preventDefault(); handleSendEnquiry(); }}>
        <div className="cm-form-group">
          <label className="cm-label" htmlFor="cm-enquiry-name">
            Full Name <span className="cm-required">*</span>
          </label>
          <input
            id="cm-enquiry-name"
            className={`cm-input${errors.enquiryName ? ' cm-input-error' : ''}`}
            type="text"
            placeholder="Enter your full name"
            value={state.enquiryName || ''}
            onChange={(e) => updateField('enquiryName', e.target.value)}
            required
          />
          {errors.enquiryName && <p className="cm-form-error">{errors.enquiryName}</p>}
        </div>

        <div className="cm-form-group">
          <label className="cm-label" htmlFor="cm-enquiry-phone">
            WhatsApp Number <span className="cm-required">*</span>
          </label>
          <input
            id="cm-enquiry-phone"
            className={`cm-input${errors.enquiryPhone ? ' cm-input-error' : ''}`}
            type="tel"
            placeholder="e.g. 08012345678"
            value={state.enquiryPhone || ''}
            onChange={(e) => updateField('enquiryPhone', e.target.value)}
            required
          />
          {errors.enquiryPhone && <p className="cm-form-error">{errors.enquiryPhone}</p>}
        </div>

        <div className="cm-form-group">
          <label className="cm-label" htmlFor="cm-enquiry-email">
            Email Address <span className="cm-optional">(optional)</span>
          </label>
          <input
            id="cm-enquiry-email"
            className="cm-input"
            type="email"
            placeholder="Enter your email address"
            value={state.enquiryEmail || ''}
            onChange={(e) => updateField('enquiryEmail', e.target.value)}
          />
        </div>

        {/* Delivery Location (merchant-configured states) */}
        {merchantStates.length > 0 && (
          <div className="cm-form-group">
            <label className="cm-label" htmlFor="cm-enquiry-state">
              Delivery Location <span className="cm-optional">(optional)</span>
            </label>
            <select
              id="cm-enquiry-state"
              className="cm-select"
              value={state.enquiryState || ''}
              onChange={(e) => updateField('enquiryState', e.target.value)}
            >
              <option value="">Select delivery location</option>
              {merchantStates.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        )}

        <div className="cm-form-group">
          <label className="cm-label" htmlFor="cm-enquiry-address">
            Delivery Address <span className="cm-optional">(optional)</span>
          </label>
          <textarea
            id="cm-enquiry-address"
            className="cm-textarea"
            placeholder="Enter your delivery address"
            value={state.enquiryAddress || ''}
            onChange={(e) => updateField('enquiryAddress', e.target.value)}
            rows={2}
          />
        </div>

        <div className="cm-form-group">
          <label className="cm-label" htmlFor="cm-enquiry-note">
            Message / Note <span className="cm-optional">(optional)</span>
          </label>
          <textarea
            id="cm-enquiry-note"
            className="cm-textarea"
            placeholder="Any additional details or questions..."
            value={state.enquiryNote || ''}
            onChange={(e) => updateField('enquiryNote', e.target.value)}
            rows={3}
          />
        </div>
      </form>

      {/* Cart summary */}
      <div className="cm-enquiry-summary">
        <p className="cm-enquiry-summary-title">Order Summary</p>
        {cartItems.map((item, index) => {
          const product = item.product;
          const variationStr = formatVariations(item.variations);
          const lineTotal = product.price * item.qty;
          const itemKey = item.variantSku || `${product.id || product._id}-${index}`;

          return (
            <div key={itemKey} className="cm-enquiry-summary-item">
              <div className="cm-enquiry-summary-item-info">
                <span className="cm-enquiry-summary-item-name">{product.name}</span>
                {variationStr && (
                  <span className="cm-enquiry-summary-item-variant">{variationStr}</span>
                )}
                <span className="cm-enquiry-summary-item-qty">x{item.qty}</span>
              </div>
              <span className="cm-enquiry-summary-item-price">{formatPrice(lineTotal)}</span>
            </div>
          );
        })}
        <div className="cm-enquiry-summary-total">
          <span className="cm-enquiry-summary-total-label">Subtotal</span>
          <span className="cm-enquiry-summary-total-value">{formatPrice(cartTotal)}</span>
        </div>
      </div>

      {/* Footer buttons */}
      <div className="cm-step-footer">
        <button type="button" className="cm-btn cm-btn-secondary" onClick={goBack}>
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        <button
          type="button"
          className="cm-whatsapp-btn"
          onClick={handleSendEnquiry}
          disabled={sending}
        >
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          <span>{sending ? 'Sending...' : 'Send Enquiry'}</span>
        </button>
      </div>
    </div>
  );
};

export default EnquiryInfoStep;
